import { getOrdersOptions } from "@/api/marketplace/getOrders";
import { useConfirmationDialog } from "@/context/ConfirmationDialog";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { ApiError } from "../types";

const API_URL = import.meta.env.VITE_API_URL;

type CancelOrderBody = {
	orderHashes: string[];
};

export const useCancelOrderMutation = () => {
	const queryClient = useQueryClient();
	const { confirm } = useConfirmationDialog();

	return useMutation<Response, ApiError, CancelOrderBody>({
		mutationFn: async (data) => {
			const ok = await confirm(
				"Cancel order",
				"Are you sure you want to cancel this order?",
				"Yes, Cancel Order",
				"No",
			);
			if (!ok) {
				throw new Error("Request Aborted");
			}

			const response = await fetch(`${API_URL}/marketplace/orders`, {
				method: "DELETE",
				credentials: "include",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(data),
			});

			if (!response.ok) {
				const error = await response.json();
				throw error;
			}

			return response.json();
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: ["marketplace", "orders"],
			});
			toast.success("Order cancelled");
		},
		onError: (error) => {
			toast.error(error.message.slice(0, 300));
		},
	});
};
